"use client";

import * as Sentry from "@sentry/nextjs";
import { ReactNode, createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useToast } from "@/components/providers/FetchToastProvider";
import { useMobile } from "@/hooks/use-mobile";
import { useUser } from "@/hooks/useUser";
import { createClient } from "@/utils/supabase/client";
import {
  clearPersistedState,
  readPersistedState,
  useSupportChatPersistence,
} from "../_hooks/useSupportChatPersistence";
import { useSupportChatSocket } from "../_hooks/useSupportChatSocket";
import { closeSupportChat } from "../_logic/close-chat";
import { MAX_MESSAGE_LENGTH } from "../_logic/constants";
import { extractEventIdFromPath } from "../_logic/extract-event-id";
import { insertAdminMessage } from "../_logic/insert-admin-message";
import { sanitizeAdminImages } from "../_logic/sanitize-images";
import { sendSupportChatMessage } from "../_logic/send-message";
import { AdminMessage, Message, SendFailureKind, ThreadStatus, UserMessage } from "../_logic/types";

export interface SupportChatContextValue {
  isOpen: boolean;
  openWidget: () => void;
  /** Hide the widget but keep the floating launcher (minimize). */
  closeWidget: () => void;
  /** Hide the widget AND the launcher — full dismissal. Sidebar still reopens. */
  dismissWidget: () => void;
  toggleWidget: () => void;
  /** True when the user fully dismissed via X; gates the floating launcher. */
  dismissed: boolean;
  messages: Message[];
  sendMessage: (text: string) => Promise<void>;
  retrySendMessage: (id: string) => Promise<void>;
  threadStatus: ThreadStatus;
  startNewChat: () => void;
  /** Sends POST /support-chat/close. Fire-and-forget; WS event drives UI transition. */
  endChat: () => Promise<void>;
  isMobile: boolean;
}

export const SupportChatContext = createContext<SupportChatContextValue | null>(null);

/**
 * Owns the live-chat state for the enterprise session: messages, thread status,
 * widget open/closed, and the socket + persistence wiring. Mounts inside the
 * enterprise shell so its lifetime spans all `/dashboard/*` routes.
 *
 * @param props - Children rendered with access to the chat context.
 * @returns Provider wrapping `children` with `SupportChatContext`.
 */
export const SupportChatProvider = ({ children }: { children: ReactNode }) => {
  // Hooks
  const { user } = useUser();
  const { showToast } = useToast();
  const userId = user?.user_id ?? null;

  // States
  const [messages, setMessages] = useState<Message[]>([]);
  const [threadStatus, setThreadStatus] = useState<ThreadStatus>("idle");
  const [isOpen, setIsOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const isMobile = useMobile();

  // Refs (mirror state so callbacks avoid stale closures without bloating deps)
  const messagesRef = useRef<Message[]>(messages);
  const threadStatusRef = useRef<ThreadStatus>(threadStatus);
  const hasFirstAdminReplyRef = useRef(false);
  const previousUserIdRef = useRef<number | null>(null);

  // Effects (hydration — must run before handlers reference the persisted state)
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);
  useEffect(() => {
    threadStatusRef.current = threadStatus;
  }, [threadStatus]);

  useEffect(() => {
    if (userId == null) return;
    const prev = previousUserIdRef.current;
    if (prev !== null && prev !== userId) {
      clearPersistedState(prev);
      setMessages([]);
      setThreadStatus("idle");
      setIsOpen(false);
      hasFirstAdminReplyRef.current = false;
    }
    previousUserIdRef.current = userId;
    const persisted = readPersistedState(userId);
    if (persisted) {
      setMessages(persisted.messages);
      setThreadStatus(persisted.threadStatus);
      hasFirstAdminReplyRef.current = persisted.messages.some((m) => m.kind === "admin");
    }
  }, [userId]);

  useSupportChatPersistence({ userId, messages, threadStatus });

  // Handlers
  const openWidget = useCallback(() => {
    setIsOpen(true);
    setDismissed(false);
  }, []);
  const closeWidget = useCallback(() => setIsOpen(false), []);
  const dismissWidget = useCallback(() => {
    setIsOpen(false);
    setDismissed(true);
  }, []);
  const toggleWidget = useCallback(() => {
    setIsOpen((v) => {
      if (!v) setDismissed(false);
      return !v;
    });
  }, []);

  const showToastForFailure = useCallback(
    (kind: SendFailureKind) => {
      if (kind === "rateLimit") showToast("Too many messages — try again in a minute", "error");
      else if (kind === "server" || kind === "network") showToast("Couldn't reach support. Please retry.", "error");
      else if (kind === "auth") showToast("Session expired. Please refresh the page.", "error");
      else if (kind === "validation") showToast("Message couldn't be sent", "error");
    },
    [showToast]
  );

  // Single bounded 401 retry — refresh session and retry once.
  const postMessage = useCallback(
    async (id: string, text: string) => {
      try {
        const supabase = createClient();
        const {
          data: { session },
        } = await supabase.auth.getSession();
        // Read pathname at send time so the user gets the right event context
        // even if they navigated between opening the widget and sending.
        const eventId = typeof window !== "undefined" ? extractEventIdFromPath(window.location.pathname) : undefined;
        let result = await sendSupportChatMessage({
          text,
          accessToken: session?.access_token ?? "",
          eventId,
        });

        if (!result.ok && result.kind === "auth") {
          const {
            data: { session: refreshed },
          } = await supabase.auth.refreshSession();
          result = await sendSupportChatMessage({
            text,
            accessToken: refreshed?.access_token ?? "",
            eventId,
          });
        }

        if (result.ok) {
          setMessages((prev) =>
            prev.map((m): Message => (m.id === id && m.kind === "user" ? { ...m, status: "sent" } : m))
          );
        } else {
          const reason = result.kind;
          setMessages((prev) =>
            prev.map(
              (m): Message => (m.id === id && m.kind === "user" ? { ...m, status: "failed", failureReason: reason } : m)
            )
          );
          showToastForFailure(reason);
        }
      } catch (err) {
        Sentry.captureException(err, { tags: { feature: "support-chat", phase: "send" } });
        setMessages((prev) =>
          prev.map(
            (m): Message =>
              m.id === id && m.kind === "user" ? { ...m, status: "failed", failureReason: "network" } : m
          )
        );
        showToastForFailure("network");
      }
    },
    [showToastForFailure]
  );

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || trimmed.length > MAX_MESSAGE_LENGTH) return;
      if (threadStatusRef.current === "closed") return;

      const id = crypto.randomUUID();
      const optimistic: UserMessage = { id, kind: "user", text: trimmed, status: "sending" };
      setMessages((prev) => [...prev, optimistic]);
      if (threadStatusRef.current === "idle") setThreadStatus("active");

      await postMessage(id, trimmed);
    },
    [postMessage]
  );

  const retrySendMessage = useCallback(
    async (id: string) => {
      const msg = messagesRef.current.find((m) => m.id === id);
      if (!msg || msg.kind !== "user") return;
      setMessages((prev) =>
        prev.map(
          (m): Message => (m.id === id && m.kind === "user" ? { ...m, status: "sending", failureReason: undefined } : m)
        )
      );
      await postMessage(id, msg.text);
    },
    [postMessage]
  );

  const handleSocketMessage = useCallback((payload: AdminMessage) => {
    // Defense in depth: cap sender.name so a compromised admin display name
    // can't disrupt UI or convincingly spoof an oversized brand string. Images
    // are rendered as links, so only https urls are kept.
    const { images, ...rest } = payload;
    const safeImages = sanitizeAdminImages(images);
    // Nothing would render — an empty bubble shouldn't land in the thread or
    // auto-open the widget.
    if (!rest.text?.trim() && !safeImages) return;
    const safePayload: AdminMessage = {
      ...rest,
      kind: "admin",
      ...(safeImages && { images: safeImages }),
      sender: {
        ...payload.sender,
        name: payload.sender.name?.slice(0, 80) || "Support",
      },
    };
    setMessages((prev) => {
      // Dedupe multi-pod fan-out via composite key. The backend sends
      // id = ts = the Slack message ts, so each reply already has its own id;
      // the composite id+ts+text key is kept so a payload that reuses an id
      // (as an older backend did with id = thread_ts) can't silently drop the
      // 2nd+ admin replies. It collapses true fan-out duplicates (all fields
      // match) while letting distinct replies through.
      const key = `${safePayload.id}|${safePayload.ts}|${safePayload.text}`;
      if (prev.some((m) => m.kind === "admin" && `${m.id}|${m.ts}|${m.text}` === key)) return prev;
      return insertAdminMessage(prev, safePayload);
    });
    if (!hasFirstAdminReplyRef.current) {
      hasFirstAdminReplyRef.current = true;
      setIsOpen(true); // auto-open: first admin reply only, per active thread
      setDismissed(false);
    }
  }, []);

  const handleSocketClosed = useCallback(() => {
    // Only transition when we currently have an active conversation. After a
    // local reset (idle), late echoes from a user-initiated close are stale
    // and would otherwise render a "closed" indicator on an empty thread.
    setThreadStatus((prev) => (prev === "active" ? "closed" : prev));
  }, []);

  const handleSignOut = useCallback(() => {
    const id = previousUserIdRef.current;
    if (id != null) clearPersistedState(id);
    setMessages([]);
    setThreadStatus("idle");
    setIsOpen(false);
    setDismissed(false);
    hasFirstAdminReplyRef.current = false;
    previousUserIdRef.current = null;
  }, []);

  const startNewChat = useCallback(() => {
    if (userId != null) clearPersistedState(userId);
    setMessages([]);
    setThreadStatus("idle");
    setDismissed(false);
    hasFirstAdminReplyRef.current = false;
  }, [userId]);

  const endChat = useCallback(async () => {
    try {
      const supabase = createClient();
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) return;
      await closeSupportChat(session.access_token);
    } catch (err) {
      Sentry.captureException(err, { tags: { feature: "support-chat", phase: "close" } });
    }
  }, []);

  useSupportChatSocket({
    enabled: userId != null,
    onMessage: handleSocketMessage,
    onClosed: handleSocketClosed,
    onSignOut: handleSignOut,
  });

  // Render
  // Memoize so unrelated parent re-renders (e.g. Zustand user-store ticks)
  // don't cascade context updates through every consumer.
  const value = useMemo<SupportChatContextValue>(
    () => ({
      isOpen,
      openWidget,
      closeWidget,
      dismissWidget,
      toggleWidget,
      dismissed,
      messages,
      sendMessage,
      retrySendMessage,
      threadStatus,
      startNewChat,
      endChat,
      isMobile,
    }),
    [
      isOpen,
      openWidget,
      closeWidget,
      dismissWidget,
      toggleWidget,
      dismissed,
      messages,
      sendMessage,
      retrySendMessage,
      threadStatus,
      startNewChat,
      endChat,
      isMobile,
    ]
  );

  return <SupportChatContext.Provider value={value}>{children}</SupportChatContext.Provider>;
};
